import { useMemo, useRef } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { computeReviewMerge } from "@productlane/lib/src/prosemirror-diff"
import { queries } from "@productlane/zero/queries"

import type {
	RawProposalGroup,
	SuggestionRow,
} from "../helpCenter/use-article-suggestions"
import {
	aggregateProposals,
	toMergeInput,
} from "../helpCenter/use-article-suggestions"

const NO_DISCARDS: ReadonlySet<string> = new Set()

// Accept reads the proposal on the server, so an edit still debouncing or in
// flight has to be saved first, or the accept applies the text from before it.
export interface DocsSaves {
	register: (settle: () => Promise<boolean>) => () => void
	// False when a save failed; its own toast already told the reviewer why.
	settle: () => Promise<boolean>
}

export function useDocsSaves(): DocsSaves {
	const pending = useRef(new Set<() => Promise<boolean>>())
	return useMemo(
		() => ({
			register: (settle) => {
				pending.current.add(settle)
				return () => {
					pending.current.delete(settle)
				}
			},
			settle: async () =>
				(await Promise.all([...pending.current].map((s) => s()))).every(
					Boolean,
				),
		}),
		[],
	)
}

export interface ArticleLike {
	id: string
	title: string
	icon: string
	content: unknown
}

export function useProposalGroupRows(proposalGroupId: string | null) {
	const [group, result] = useQuery(
		queries.proposalGroupById(proposalGroupId ?? ""),
		{ enabled: !!proposalGroupId },
	)
	const rows = useMemo(
		() =>
			group
				? aggregateProposals({
						standalone: [],
						groups: [group as unknown as RawProposalGroup],
					}).rows
				: [],
		[group],
	)
	const articles = useMemo(() => {
		const byId = new Map<string, ArticleLike>()
		for (const p of group?.proposals ?? []) {
			const article = p.article as ArticleLike | undefined
			if (article) byId.set(article.id, article)
		}
		return byId
	}, [group])
	return { group, rows, articles, loaded: result.type === "complete" }
}

export function useRowMerge(row: SuggestionRow, article?: ArticleLike) {
	const baseHtml =
		row.articleId && typeof article?.content === "string" ? article.content : ""
	const merged = useMemo(
		() =>
			computeReviewMerge(
				baseHtml,
				row.proposals.map(toMergeInput),
				NO_DISCARDS,
			),
		[baseHtml, row.proposals],
	)
	const proposedTitle = row.proposals.find(
		(p) => p.proposedTitle,
	)?.proposedTitle
	const title = proposedTitle ?? article?.title ?? row.title
	return { baseHtml, merged, title }
}
